import { useCallback, useEffect, useState } from 'react'
import { toast } from '@sales-center/components'
import { ENTITY_TYPES, type IContact } from '@sales-center/common'

import { ILeadDetail, pushContact as leadPushContact } from '@user/store/lead'
import {
  IOpportunityDetail,
  pushContact as opportunityPushContact,
} from '@user/store/opportunity'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { ContactsPageProps } from '../ContactsPage'
import { useRemoveContact } from './useRemoveContact'
import { useUpdateContact } from './useUpdateContact'
import { LocationOrContact } from '../../hooks/useCleanupEntityContacts'
import { IMoreMenuItem } from '@sales-center/components'
import { useGetExternalAppName } from '@user/store/authorization/hooks/useGetExternalAppName'
import { CardVariants } from '../../ContactCard'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { validateContactForPush } from '../functions'

export function useActions(
  params: ContactsPageProps,
  wwmContactsCount: number,
  detail?: ILeadDetail | IOpportunityDetail,
) {
  const { entityId, entityType, initialTab, canUpdateEntity } = params

  const dispatch = useAppDispatch()

  const appName = useGetExternalAppName()
  const { clientUrl } = useSelector(selectCurrentUser)

  const externalLinkId = detail?.serviceContact.externalLinkId
  const allowSendToLocation = !!externalLinkId

  const onClickSendToLocation = useCallback(
    (contact: IContact) => {
      if (!!externalLinkId) {
        const func =
          entityType === ENTITY_TYPES.Lead
            ? leadPushContact
            : opportunityPushContact

        const errors = validateContactForPush(contact)

        if (errors.length > 0) {
          toast.error(errors.join('\r\n'))
          return
        }

        dispatch(
          func({
            entityId,
            contactId: contact.id,
            locationId: externalLinkId,
          }),
        )
      } else {
        toast.error(`Push contact failed`)
      }
    },
    [dispatch, entityId, entityType, externalLinkId],
  )

  const [activeTab, setActiveTab] = useState(
    initialTab ?? (wwmContactsCount > 0 ? 1 : 2),
  )

  useEffect(() => {
    setActiveTab(initialTab ?? (wwmContactsCount > 0 ? 1 : 2))
  }, [initialTab, wwmContactsCount])

  const onChangeTab = useCallback((_, value: number) => setActiveTab(value), [])

  const { onClickDelete, ...removeContactState } = useRemoveContact()

  const { onClickEdit, ...updateContactState } = useUpdateContact(detail)

  const onClickUrl = useCallback(
    (locationUrl) => void window.open(locationUrl, '_blank'),
    [],
  )

  const getMenuItems = useCallback<
    (
      item: LocationOrContact,
      variant: CardVariants,
    ) => IMoreMenuItem[] | undefined
  >(
    (item, variant) => {
      if (!canUpdateEntity) return

      const {
        type,
        data: { id: secondaryContactId, contact },
      } = item

      const {
        id: contactId,
        externalLinkType: linkType,
        externalLinkId: linkId,
      } = contact

      const locationUrl = linkType
        ? linkType === 'Contact'
          ? `${clientUrl}/${linkType}/detail.asp?Mode=Edit&${linkType}Id=${linkId}`
          : `${clientUrl}/${linkType}/edit.asp?Mode=Edit&${linkType}Id=${linkId}`
        : ''

      const isLocation = type === 'location'

      const deleteTooltip = isLocation
        ? `You cannot remove locations from here`
        : contactId === detail?.primaryContact.id
        ? 'Designate another contact as primary to remove this one'
        : undefined

      const sendTooltip = !!detail?.billToContact.externalLinkId
        ? undefined
        : `You must first match the ${entityType.toLocaleLowerCase()} to a location`

      return [
        {
          label: 'Manage in PestPac',
          onClick: () => onClickUrl(locationUrl),
          hidden: variant !== CardVariants.pestpac,
          style: { color: '#005AA3' },
        },
        {
          label: 'Edit',
          onClick: () => onClickEdit(contactId, item),
          tooltipText: '',
          hidden: variant === CardVariants.pestpac,
          dataTestId: 'editContactOptionBtn',
        },
        {
          label: 'Remove',
          onClick: () =>
            !!secondaryContactId && onClickDelete(secondaryContactId),
          disabled: !!deleteTooltip,
          tooltipText: deleteTooltip,
          hidden: variant === CardVariants.pestpac,
          dataTestId: 'removeContactOptionBtn',
        },
        {
          label: `Send To ${appName || 'External'} Location`,
          onClick: () => onClickSendToLocation(contact),
          disabled: !!sendTooltip,
          tooltipText: sendTooltip,
          hidden: isLocation || variant === CardVariants.pestpac,
        },
      ]
    },
    [
      appName,
      canUpdateEntity,
      detail?.billToContact.externalLinkId,
      detail?.primaryContact.id,
      entityType,
      onClickDelete,
      onClickEdit,
      onClickSendToLocation,
      onClickUrl,
      clientUrl,
    ],
  )

  return {
    appName,

    allowSendToLocation,
    onClickSendToLocation,

    activeTab,
    onChangeTab,

    ...removeContactState,
    ...updateContactState,

    getMenuItems,
  }
}
